import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  X, AlertTriangle,
  LayoutDashboard, ClipboardList, ShieldCheck, BarChart3,
  PenLine, MessageSquare, Accessibility, Bot,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import FlowBar from './FlowBar';
import Eyebrow from './Eyebrow';
import { DraftyMascot } from './DraftyMascot';

const LOGO_URL = '/images/DraftFlow-billboard.png';

/**
 * Pre-login landing page — PHASE-1.5-BRAND-SPEC.md §7, exact composition:
 * FlowBar(8) → dark header strip → hero (ink-950) → cream band (3 cards)
 * → dark footer → FlowBar(8). Copy is final per spec; cream-band card
 * headlines/bodies pending ruling Q6 (cards render only spec-given strings).
 */
export const LandingPage = () => {
  const { signInDemo, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState('');

  // Show auth rejection errors (human-readable, set by AuthContext)
  useEffect(() => {
    const error = sessionStorage.getItem('draftflow_auth_error');
    if (error) {
      setAuthError(error);
      sessionStorage.removeItem('draftflow_auth_error');
    }
  }, []);

  // §5 status-chip-style label on light surfaces: chip token bg, ink-900 text
  const CardChip = ({ bg, label }) => (
    <span
      className={`inline-block ${bg} text-ink-900 font-medium`}
      style={{
        fontFamily: 'var(--font-eyebrow)',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        padding: '4px 10px',
        borderRadius: '6px',
      }}
    >
      {label}
    </span>
  );

  return (
    <div className="min-h-screen bg-ink-950 flex flex-col">
      {/* 1. FlowBar (8px) */}
      <FlowBar height={8} />

      {/* 2. Dark header strip */}
      <header className="bg-ink-950 px-6 py-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <img src={LOGO_URL} alt="DraftFlow" className="h-12" />
          <Eyebrow label="BUILT BY A LITERACY INTERVENTIONIST" tone="gold" />
        </nav>
      </header>

      {/* Auth error banner (functional ugly-path surface — renders only on error) */}
      {authError && (
        <div className="px-6 pt-2">
          <div className="max-w-3xl mx-auto bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="flex-1 text-red-300 font-medium text-sm">{authError}</p>
            <button onClick={() => setAuthError('')} className="text-red-400 hover:text-red-300 p-1" aria-label="Dismiss">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 3. Hero on ink-950 — 2-column (Q8a). Left: copy + buttons (unchanged).
          Right: product card with Drafty overlapping. Mobile: visual stacks
          BELOW copy so sign-in stays above the fold (DOM order copy-then-visual). */}
      <section className="bg-ink-950 px-6 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          {/* Left column — copy + buttons */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <div className="flex justify-center md:justify-start">
              <Eyebrow label="THE AUTHENTIC WRITING PLATFORM" tone="teal" />
            </div>
            <h1 className="font-display font-extrabold text-cream text-4xl md:text-6xl leading-tight mt-6">
              Every Student Writes. The <span className="text-gold-bright">Coach</span> Never Does.
            </h1>
            <p className="font-accent-serif text-text-secondary-dark text-xl md:text-2xl mt-6">
              A Socratic AI writing coach that asks, challenges, and motivates — and never writes a single word for a student.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <button
                data-testid="google-signin-btn"
                onClick={() => signInWithGoogle()}
                className="bg-flow-blue text-white hover:bg-flow-blue-hover px-8 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#FFFFFF" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#FFFFFF" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".8"/>
                  <path fill="#FFFFFF" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".6"/>
                  <path fill="#FFFFFF" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity=".9"/>
                </svg>
                Sign in with Google
              </button>
              <button
                data-testid="try-demo-btn"
                onClick={() => signInDemo('teacher')}
                className="border border-border-secondary text-cream hover:border-flow-blue-hover px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Try the demo
              </button>
            </div>
            {/* Quiet secondary student entry (Q11 ruling 3a) */}
            <button
              data-testid="student-join-link"
              onClick={() => navigate('/join')}
              className="mt-5 text-text-secondary-dark hover:text-cream text-sm underline underline-offset-4 transition-colors"
            >
              Student with a class code? Join your class →
            </button>
          </motion.div>

          {/* Right column — product card + Drafty overlap */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex justify-center"
          >
            <div className="relative max-w-lg w-full">
              <div className="absolute inset-0 bg-ai-accent/20 blur-3xl rounded-full" />
              <img
                src="/images/draftflow-hero-card.webp"
                alt="DraftFlow: an active writing assignment with Drafty, the AI coach, alongside the student's draft"
                fetchpriority="high"
                className="relative z-10 w-full rounded-2xl border border-surface-raised shadow-2xl shadow-ink-950/60"
                data-testid="hero-card"
              />
              <DraftyMascot
                size="lg"
                mood="excited"
                className="absolute z-20 -bottom-10 -right-6 !w-36 !h-36 drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. Cream band — three cards (copy final per Fable ruling Q6) */}
      <section className="bg-cream px-6 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            {
              bg: 'bg-teal-chip',
              label: 'SOCRATIC BY DESIGN',
              headline: 'The Coach Asks. Students Write.',
              body: "DraftFlow's AI coach questions, challenges, and motivates — and never writes a single word for a student. Every sentence in the draft is the student's own.",
            },
            {
              bg: 'bg-flow-blue-chip',
              label: 'TEACHER FINAL SAY',
              headline: 'Nothing Reaches a Student Without You.',
              body: 'AI feedback waits in your review queue until you approve, edit, or reject it. You stay the teacher of record — DraftFlow does the first pass.',
            },
            {
              bg: 'bg-chip-gold',
              label: 'PROCESS YOU CAN PROVE',
              headline: 'Every Draft Tells Its Story.',
              body: 'Revision history and coach transcripts show exactly how the writing happened. No AI-detection guesswork — just visible, verifiable process.',
            },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-xl border border-ink-900/5 shadow-sm p-6">
              <CardChip bg={card.bg} label={card.label} />
              <h3 className="font-display font-bold text-ink-900 text-lg mt-4">
                {card.headline}
              </h3>
              <p className="text-ink-900/70 text-sm leading-relaxed mt-2">
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Teacher Features — ink-950 (copy verbatim from commit 0a2f4ea;
          DraftGuard body replaced per Q8(d); emoji → lucide icons per Q8(b)) */}
      <section className="bg-ink-950 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-3"><Eyebrow label="FOR TEACHERS" tone="gold" /></div>
          <h2 className="font-display font-bold text-cream text-3xl md:text-4xl text-center">
            Built for Teachers
          </h2>
          <p className="text-text-secondary-dark text-center mt-4 mb-14 max-w-2xl mx-auto">
            Everything you need to run a writing-centered ELA classroom, from assignment creation to standards-aligned analytics.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: LayoutDashboard, tint: 'text-flow-blue', bg: 'bg-flow-blue/15', title: 'DraftBoard', body: 'Your command center for managing assignments and monitoring progress' },
              { Icon: ClipboardList, tint: 'text-gold-bright', bg: 'bg-gold-bright/15', title: 'FlowRubric', body: 'AI-generated 4-point rubrics aligned to CCSS ELA standards' },
              { Icon: ShieldCheck, tint: 'text-flow-blue', bg: 'bg-flow-blue/15', title: 'DraftGuard', body: "Revision timelines and coach transcripts give you clear evidence of each student's authentic writing process — no unreliable AI-detection scores." },
              { Icon: BarChart3, tint: 'text-gold-bright', bg: 'bg-gold-bright/15', title: 'DraftData', body: 'CCSS mastery analytics with radar charts and growth tracking' },
            ].map((f) => (
              <div key={f.title} className="bg-surface-raised rounded-xl border border-white/5 p-6">
                <div className={`w-12 h-12 rounded-lg ${f.bg} ${f.tint} flex items-center justify-center mb-4`}>
                  <f.Icon size={24} aria-hidden="true" />
                </div>
                <h3 className="font-display font-semibold text-cream text-lg mb-2">{f.title}</h3>
                <p className="text-text-secondary-dark text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Student Features — cream (copy verbatim from 0a2f4ea; lucide icons
          teal-flow, DraftyChat ai-accent per Q8b) */}
      <section className="bg-cream px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-3"><Eyebrow label="FOR STUDENTS" tone="teal" light /></div>
          <h2 className="font-display font-bold text-ink-900 text-3xl md:text-4xl text-center">
            What Your Students Get
          </h2>
          <p className="text-ink-900/70 text-center mt-4 mb-14 max-w-2xl mx-auto">
            AI coaching that builds writing capacity without doing the work for them. Strict Coach Mode means no ghostwriting — ever.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: PenLine, tint: 'text-teal-deep', bg: 'bg-teal-chip', title: 'DraftSpace', body: 'Rich text editor with auto-save and real-time word count' },
              { Icon: MessageSquare, tint: 'text-teal-deep', bg: 'bg-teal-chip', title: 'DraftBack', body: 'AI coaching feedback — guides without writing for them' },
              { Icon: Accessibility, tint: 'text-teal-deep', bg: 'bg-teal-chip', title: 'DraftAssist', body: '504/IEP accommodations built in — TTS, sentence starters, extended time' },
              { Icon: Bot, tint: 'text-ai-accent', bg: 'bg-chip-lavender', title: 'DraftyChat', body: 'AI writing coach that knows what your student is working on' },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-ink-900/5 shadow-sm p-6">
                <div className={`w-12 h-12 rounded-lg ${f.bg} ${f.tint} flex items-center justify-center mb-4`}>
                  <f.Icon size={24} aria-hidden="true" />
                </div>
                <h3 className="font-display font-semibold text-ink-900 text-lg mb-2">{f.title}</h3>
                <p className="text-ink-900/70 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. How It Works — ink-950 (copy verbatim from 0a2f4ea; Drafty returns) */}
      <section className="bg-ink-950 px-6 py-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow label="HOW IT WORKS" tone="blue" />
            <h2 className="font-display font-bold text-cream text-3xl md:text-4xl mt-3 mb-8">
              Up and Running in Minutes
            </h2>
            <ol className="space-y-6">
              {[
                { title: 'Create your class', desc: 'Sign in with Google and get a unique join code to share with students.' },
                { title: 'Build assignments with CCSS standards', desc: 'Create prompts, attach rubrics, and align to ELA standards.' },
                { title: 'Share the code — students join and start writing', desc: 'Students enter your class code or click a direct assignment link.' },
                { title: 'Monitor, review, and track growth', desc: 'Use DraftGuard, DraftData, and DraftTrack to monitor progress.' },
              ].map((item, i) => (
                <li key={item.title} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-flow-blue/20 text-flow-blue-hover flex items-center justify-center font-mono font-bold text-sm">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-cream font-medium">{item.title}</p>
                    <p className="text-text-secondary-dark text-sm mt-1">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-flow-blue/10 blur-3xl rounded-full" />
              <DraftyMascot size="xl" mood="thinking" className="relative z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* 8. Secondary CTA — cream (copy verbatim from 0a2f4ea) */}
      <section className="bg-cream px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-bold text-ink-900 text-3xl md:text-4xl mb-4">
            Ready to Transform Your ELA Classroom?
          </h2>
          <p className="text-ink-900/70 text-lg mb-8">
            Set up your first class in under a minute. Your students are one code away.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="cta-google-signin"
              onClick={() => signInWithGoogle()}
              className="bg-flow-blue text-white hover:bg-flow-blue-hover px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Sign in with Google
            </button>
            <button
              data-testid="cta-try-demo"
              onClick={() => signInDemo('teacher')}
              className="border border-ink-900/20 text-ink-900 hover:border-flow-blue px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Try the demo
            </button>
          </div>
          <button
            data-testid="cta-student-join-link"
            onClick={() => navigate('/join')}
            className="mt-5 inline-flex items-center gap-1 text-teal-deep hover:text-ink-900 text-sm underline underline-offset-4 transition-colors"
          >
            Student with a class code? Join your class <ArrowRight size={14} />
          </button>
        </div>
      </section>

      {/* 9. Dark footer */}
      <footer className="bg-ink-950 px-6 py-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-center md:text-left">
            <p className="text-cream text-sm">DraftFlow — a Classroom Ready AI product</p>
            <p className="text-slate-500 text-xs mt-1">
              <Link to="/trust" className="hover:text-cream underline">Student Data &amp; Privacy</Link>
              <span className="mx-2">·</span>
              <Link to="/privacy" className="hover:text-cream underline">Privacy Policy</Link>
              <span className="mx-2">·</span>
              <Link to="/terms" className="hover:text-cream underline">Terms of Service</Link>
            </p>
          </div>
          <span className="font-mono text-gold-bright text-sm">draftflow.pro</span>
        </div>
      </footer>

      {/* FlowBar (8px) at the very bottom */}
      <FlowBar height={8} />
    </div>
  );
};

export default LandingPage;
